import { useEffect, useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { brl } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ADDON_GROUP = "Adicionais";

export const ComboDialog = ({ open, onOpenChange, product }) => {
  const { setQty } = useCart();
  const groups = product?.option_groups || [];
  const addons = product?.addons || [];
  const [picked, setPicked] = useState({});
  const [qty, setQtyLocal] = useState(1);

  useEffect(() => { if (open) { setPicked({}); setQtyLocal(1); } }, [open, product]);

  const changeAddon = (a, delta) =>
    setPicked((p) => {
      const k = `${ADDON_GROUP}|${a.name}`;
      const value = (p[k] || 0) + delta;
      const next = { ...p, [k]: value };
      if (value <= 0) delete next[k];
      return next;
    });

  const totalIn = (g) => g.options.reduce((s, o) => s + (picked[`${g.name}|${o.name}`] || 0), 0);
  const change = (g, o, delta) =>
    setPicked((p) => {
      const k = `${g.name}|${o.name}`;
      const value = (p[k] || 0) + delta;
      if (value < 0) return p;
      if (delta > 0 && totalIn(g) >= g.max) return p;
      const next = { ...p, [k]: value };
      if (!value) delete next[k];
      return next;
    });

  const { pendingGroup, extras } = useMemo(() => {
    let extra = 0;
    groups.forEach((g) => g.options.forEach((o) => { extra += (picked[`${g.name}|${o.name}`] || 0) * (o.price || 0); }));
    addons.forEach((a) => { extra += (picked[`${ADDON_GROUP}|${a.name}`] || 0) * (a.price || 0); });
    return { pendingGroup: groups.find((g) => totalIn(g) < g.min), extras: extra };
  }, [groups, addons, picked]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = () => {
    if (pendingGroup) return toast.error(`Escolha ${pendingGroup.min} em "${pendingGroup.name}"`);
    const selections = Object.entries(picked).map(([k, qty]) => {
      const [group, option] = k.split("|");
      const price = group === ADDON_GROUP
        ? addons.find((a) => a.name === option)?.price || 0
        : groups.find((g) => g.name === group)?.options.find((o) => o.name === option)?.price || 0;
      return { group, option, qty, price };
    });
    setQty(product, qty, selections);
    onOpenChange(false);
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  if (!product) return null;
  const total = (product.price + extras) * qty;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="combo-dialog" className="max-h-[90vh] overflow-y-auto bg-[#1A1A1A]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl leading-tight">{product.name}</DialogTitle>
          <DialogDescription className="sr-only">Escolha as opções do produto</DialogDescription>
        </DialogHeader>
        {product.description && <p className="text-sm text-slate-400">{product.description}</p>}
        <p className="font-display text-2xl font-bold text-primo-400">{brl(product.price)}</p>

        <div className="space-y-5">
          {groups.map((g) => {
            const total = totalIn(g);
            const done = total >= g.min;
            return (
              <div key={g.name} data-testid={`combo-group-${g.name}`}>
                <div className="flex items-center justify-between gap-2 border-b border-white/[0.08] pb-2">
                  <div>
                    <p className="font-semibold uppercase tracking-wide">{g.name}</p>
                    <p className="text-xs text-slate-500">
                      {g.required ? "" : "Opcional · "}
                      {g.min === g.max ? `Escolha ${g.max} item(ns)` : `Até ${g.max} item(ns)`}
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${done ? "bg-emerald-500/15 text-emerald-400" : "bg-primo-500 text-white"}`}>
                    {total}/{g.max}
                  </span>
                </div>
                <div className="divide-y divide-white/[0.05]">
                  {g.options.map((o) => {
                    const qty = picked[`${g.name}|${o.name}`] || 0;
                    return (
                      <div key={o.name} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="flex min-w-0 items-center gap-3">
                          {o.image_url && <img src={o.image_url} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />}
                          <div className="min-w-0">
                            <p className="truncate text-sm">{o.name}</p>
                            {o.price > 0 && <p className="text-xs font-semibold text-primo-400">+ {brl(o.price)}</p>}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {qty > 0 && (
                            <>
                              <button type="button" data-testid={`combo-minus-${o.name}`} onClick={() => change(g, o, -1)}
                                className="grid h-7 w-7 place-items-center rounded-full bg-white/10 hover:bg-white/20"><Minus className="h-3.5 w-3.5" /></button>
                              <span data-testid={`combo-qty-${o.name}`} className="w-4 text-center text-sm font-bold">{qty}</span>
                            </>
                          )}
                          <button type="button" data-testid={`combo-plus-${o.name}`} disabled={total >= g.max} onClick={() => change(g, o, 1)}
                            className="grid h-7 w-7 place-items-center rounded-full bg-primo-500 text-white disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {addons.length > 0 && (
          <div data-testid="addons-section">
            <div className="flex items-center justify-between gap-2 border-b border-white/[0.08] pb-2">
              <div>
                <p className="font-semibold uppercase tracking-wide">Adicionais</p>
                <p className="text-xs text-slate-500">Opcional · cobrado à parte</p>
              </div>
            </div>
            <div className="divide-y divide-white/[0.05]">
              {addons.map((a) => {
                const qty = picked[`${ADDON_GROUP}|${a.name}`] || 0;
                return (
                  <div key={a.name} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      {a.image_url && <img src={a.image_url} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />}
                      <div className="min-w-0">
                        <p className="truncate text-sm">{a.name}</p>
                        <p className="text-xs font-semibold text-primo-400">+ {brl(a.price || 0)}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {qty > 0 && (
                        <>
                          <button type="button" data-testid={`addon-minus-${a.name}`} onClick={() => changeAddon(a, -1)}
                            className="grid h-7 w-7 place-items-center rounded-full bg-white/10 hover:bg-white/20"><Minus className="h-3.5 w-3.5" /></button>
                          <span data-testid={`addon-qty-${a.name}`} className="w-4 text-center text-sm font-bold">{qty}</span>
                        </>
                      )}
                      <button type="button" data-testid={`addon-plus-${a.name}`} onClick={() => changeAddon(a, 1)}
                        className="grid h-7 w-7 place-items-center rounded-full bg-primo-500 text-white"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {extras > 0 && (
          <p data-testid="combo-extras" className="text-sm text-slate-400">Adicionais: <span className="font-semibold text-primo-400">{brl(extras)}</span></p>
        )}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-black/30 p-3">
          <span className="text-sm font-semibold">Quantidade</span>
          <div className="flex items-center gap-3">
            <button type="button" data-testid="product-qty-minus" disabled={qty <= 1} onClick={() => setQtyLocal((q) => Math.max(1, q - 1))}
              className="grid h-9 w-9 place-items-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30"><Minus className="h-4 w-4" /></button>
            <span data-testid="product-qty-value" className="w-6 text-center font-display text-lg font-bold">{qty}</span>
            <button type="button" data-testid="product-qty-plus" disabled={qty >= product.stock} onClick={() => setQtyLocal((q) => Math.min(product.stock, q + 1))}
              className="grid h-9 w-9 place-items-center rounded-full bg-primo-500 text-white hover:bg-primo-400 disabled:opacity-30"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
        <button type="button" data-testid="combo-confirm-button" onClick={confirm} disabled={!!pendingGroup}
          className="w-full rounded-full bg-primo-500 py-3.5 font-display font-bold text-white hover:bg-primo-400 disabled:opacity-40">
          {pendingGroup ? `Escolha ${pendingGroup.min} em ${pendingGroup.name}` : `Adicionar · ${brl(total)}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
